import React, { useEffect, useState } from 'react';

import PageWrapper from '../PageWrapper';
import Page from '../Page';
import { RouteComponentProps } from 'react-router';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';

import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import { MenuBar } from '@go-client/components';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  BubbleAccordion,
  BubbleBadge,
  BubbleButton,
  BubbleDrawer,
  BubbleFormGroup,
  BubbleTypeahead,
  BubbleOverlay,
  BubbleTable,
} from '@go-client/components/BubbleUI';
import ReserveNavigation from '@go-client/components/Navigations/ReserveNavigation';
import ReserveSubNavigation from '@go-client/components/Navigations/ReserveSubNavigation';
import ReservationPath, {
  ReservationFormMode,
} from '@go-client/components/ReservationPath/ReservationPath';
import {
  FlattenedReservation,
  NeedsAttentionItem,
  NeedsAttentionType,
  Reservation,
} from '@go-shared/types/reservation';
import { findFlagged, get as getReservation } from '@go-client/lib/api/reservation';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '@go-client/components/BubbleUI/BubbleTable';
import { getHourFormat } from '@go-shared/lib/property';
import { mapBy, notUndefined } from '@go-shared/lib/functional';
import { getSimpleUsers } from '@go-client/lib/api/user';
import { SimpleUser, User } from '@go-shared/types/user';
import { makeFullName, safeIdCompare } from '@go-shared/lib/utils';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { canHostEvents, canManageAllReservations } from '@go-shared/lib/permissions';
import { ReservationTableFields, ReservationTableItem } from './ReserveOngoing';
import { BubbleTypeaheadValue } from '@go-client/components/BubbleUI/BubbleTypeahead';
import {
  makeReservationTableFields,
  makeReservationTableItem,
  makeReservationTableTemplates,
} from './ReserveTransactions';
import qslib from '@go-shared/lib/querystring';
import { summarizeOccurrences } from '@go-shared/lib/reserve';
import { BubbleBadgeProps } from '@go-client/components/BubbleUI/BubbleBadge';
import { getLocaleDateStringUTC, isoToDateUTC } from '@go-shared/lib/time';
import { usePublicServerInfo } from '@go-client/context/PublicServerInfoContext';

interface ReserveFlaggedParams {
  property: string;
}

type ReserveFlaggedProps = RouteComponentProps<ReserveFlaggedParams>;

interface FlaggedTableItem {
  id: string;
  type: NeedsAttentionType;
  event: string;
  date: string;
  rental?: string;
  host?: string;
  reservationCount: number;
  _item: NeedsAttentionItem;
}

const BADGE_CONFIG: Record<
  NeedsAttentionType,
  { label: string; variant: BubbleBadgeProps['variant'] }
> = {
  overcapacity: { label: 'Overcapacity', variant: 'crimson' },
  unassigned: { label: 'Unassigned', variant: 'banana' },
  'wrong-dayspan': { label: 'Wrong Dayspan', variant: 'dirt' },
  'missing-rental': { label: 'Missing Rental', variant: 'banana' },
  'mismatching-times': { label: 'Mismatching Times', variant: 'grape' },
  'mismatching-hosts-or-rentals': { label: 'Mismatching Rental/Host', variant: 'grape' },
};

interface FlaggedFilters {
  selectedHosts: Array<BubbleTypeaheadValue<SimpleUser>>;
}

function makeDefaultFilters(user: User): FlaggedFilters {
  const selectedHosts: Array<BubbleTypeaheadValue<SimpleUser>> = [];

  if (canHostEvents(user)) {
    selectedHosts.push({ key: user._id, item: user, refKey: user._id });
  }

  return { selectedHosts };
}

export default function ReserveFlagged(props: ReserveFlaggedProps) {
  const { activeProperty: property } = useActiveProperty();
  const { user } = useUserInfo();
  const hourFormat = getHourFormat(property);
  const { serverDate } = usePublicServerInfo();

  const [loading, setLoading] = useState(true);
  const [changingStatus, setChangingStatus] = useState(false);
  const [instances, setInstances] = useState<NeedsAttentionItem[]>([]);
  const [users, setUsers] = useState<SimpleUser[]>([]);
  const [expandedId, setExpandedId] = useState<string | undefined>(undefined);
  const [mode, _setMode] = useState<ReservationFormMode | undefined>(undefined);
  const [showDelete, setShowDelete] = useState<Record<string, boolean>>({});
  const [filters, setFilters] = useState<FlaggedFilters>(makeDefaultFilters(user));

  async function fetchData() {
    setLoading(true);
    try {
      const [fetchedUsers, data] = await Promise.all([
        getSimpleUsers(property._id),
        findFlagged(property._id),
      ]);
      setUsers(fetchedUsers.filter(canHostEvents));
      setInstances(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData().catch(console.error);
  }, [property]);

  function handleOpenReservationPanel(newMode: ReservationFormMode) {
    _setMode(newMode);
    if (newMode.type === 'edit') {
      qslib.writeQs({ reservation: newMode.edit._id });
    }
  }

  async function refreshSelectedReservation() {
    const toSelect = qslib.readQs().reservation as string | undefined;
    if (!toSelect) return;
    const reservation = await getReservation(property._id, toSelect);
    handleOpenReservationPanel({ type: 'edit', edit: reservation });
  }

  async function handleEditReservationById(id: string) {
    qslib.writeQs({ reservation: id });
    await refreshSelectedReservation();
  }

  function handleCloseReservationPanel() {
    _setMode(undefined);
    qslib.writeQs({});
  }

  function handleSubmitReservation(newReservation: Reservation) {
    fetchData().catch(console.error);
    handleOpenReservationPanel({ type: 'edit', edit: newReservation });
  }

  const usersDict = mapBy(users, '_id');

  const filteredInstances =
    filters.selectedHosts.length > 0
      ? instances.filter(item =>
          item.reservations.some(r =>
            filters.selectedHosts.some(sh => safeIdCompare(sh.item?._id, r.host))
          )
        )
      : instances;

  const hostOpts = users.map(u => ({ key: u._id, item: u, refKey: u._id }));

  const tableItems: FlaggedTableItem[] = filteredInstances.map((item, i) => {
    const firstRes = item.reservations[0];
    const ticketsById = mapBy(firstRes?.eventSnapshot.reservables.tickets ?? [], '_id');

    const summary = summarizeOccurrences(item.reservations, ticketsById, usersDict, {
      hourFormat,
      condenseDates: true,
    });

    return {
      id: `${item.type}_${item.date}_${i}`,
      type: item.type,
      date: getLocaleDateStringUTC(isoToDateUTC(item.date)),
      event: item.event,
      rental: summary.rentals || undefined,
      host: summary.hosts || undefined,
      reservationCount: item.reservations.length,
      _item: item,
    };
  });

  const tableFields: BubbleTableFields<FlaggedTableItem>[] = [
    { key: 'type', label: 'Issue', behavior: BubbleTableFieldBehavior.MinWidth },
    { key: 'reservationCount', label: 'Reservations', behavior: BubbleTableFieldBehavior.MinWidth },
    { key: 'date', label: 'Date' },
    { key: 'host', label: 'Rental / Host' },
    { key: 'event', label: 'Experience' },
    { key: 'toggle', label: '', behavior: BubbleTableFieldBehavior.Condense },
  ];

  // highlights the clicked row itself while its details are expanded, matching the same
  // pattern ReserveOngoing uses (makeDefaultListViewTemplateTdProps/makeDefaultGridViewTemplateTdProps)
  function makeDefaultFlaggedTemplateTdProps(
    data: FlaggedTableItem,
    fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>
  ) {
    return {
      ...fieldTdProps,
      className: [
        fieldTdProps && fieldTdProps.className ? fieldTdProps.className : undefined,
        expandedId === data.id ? 'bg-bubblegum-lightest' : undefined,
      ]
        .filter(notUndefined)
        .join(' '),
    };
  }

  const tableTemplates: BubbleTableTemplate<FlaggedTableItem>[] = [
    {
      key: 'type',
      tdProps: makeDefaultFlaggedTemplateTdProps,
      render: item => {
        const cfg = BADGE_CONFIG[item.type];
        return (
          <BubbleBadge variant={cfg.variant} className="mt-1 mr-1">
            {cfg.label}
          </BubbleBadge>
        );
      },
    },
    {
      key: 'reservationCount',
      tdProps: makeDefaultFlaggedTemplateTdProps,
    },
    {
      key: 'date',
      tdProps: makeDefaultFlaggedTemplateTdProps,
    },
    {
      key: 'host',
      tdProps: makeDefaultFlaggedTemplateTdProps,
      render: ({ host, rental }) =>
        host || rental ? (
          <div>
            {rental && <div>{rental}</div>}
            <div className={rental ? 'text-xs text-slate' : ''}>{host}</div>
          </div>
        ) : null,
    },
    {
      key: 'event',
      tdProps: makeDefaultFlaggedTemplateTdProps,
    },
    {
      key: 'toggle',
      tdProps: makeDefaultFlaggedTemplateTdProps,
      render: item => (
        <BubbleButton
          className="p-2-i"
          variant="navy"
          inverse
          onClick={e => {
            e.stopPropagation();
            setExpandedId(expandedId === item.id ? undefined : item.id);
          }}
          icon={
            expandedId === item.id
              ? 'arrow-down-left-and-arrow-up-right-to-center'
              : 'arrow-up-right-and-arrow-down-left-from-center'
          }
        />
      ),
    },
  ];

  const reservationTableFields: ReservationTableFields[] = makeReservationTableFields();

  const reservationTableTemplates: BubbleTableTemplate<
    ReservationTableItem<FlattenedReservation>
  >[] = [
    ...makeReservationTableTemplates({
      changingStatus,
      setChangingStatus,
      showDelete,
      setShowDelete,
      fetchData,
      onEdit: res => handleOpenReservationPanel({ type: 'edit', edit: res }),
    }),
  ];

  function tableDetailsRender(item: FlaggedTableItem) {
    const subItems = item._item.reservations.map(r =>
      makeReservationTableItem(r, usersDict, hourFormat, [], serverDate)
    );

    return (
      <div className="my-4">
        <BubbleTable
          striped
          responsive
          headVariant="turquoise"
          fields={reservationTableFields}
          templates={reservationTableTemplates}
          items={subItems}
          keyExtractor={entry => `${entry.id}-${entry.date}`}
          onRowClick={entry => handleEditReservationById(entry.sourceReservationId)}
        />
      </div>
    );
  }

  return (
    <PageWrapper {...props}>
      <Page className="Reservables absolute flex items-stretch">
        <MenuBar {...props} property={property} />
        <div className="SideMenu bg-turquoise-darkest flex flex-col">
          <div className="bg-white flex flex-col items-center">
            <BrandLogo logo={property.brand.logo} />
            <PropertyBanner {...props} property={property} swappable />
          </div>
          <div className="flex flex-col relative items-stretch p-4 bg-turquoise-darker">
            <BubbleButton
              variant="white"
              inverse
              className="text-left border border-white"
              icon="repeat"
              onClick={() => fetchData().catch(console.error)}
            >
              Refresh Data
            </BubbleButton>
          </div>
          <BubbleAccordion
            variant="turquoise"
            className="flex-grow flex flex-col border-t border-turquoise-darkest"
            labels={['Filters']}
            open={[0]}
            locked={[0]}
          >
            <div className="grid gap-4 py-4">
              {canManageAllReservations(user) && (
                <BubbleFormGroup label="Host" labelClassName="text-white-i" className="px-4">
                  <BubbleTypeahead
                    block
                    variant="turquoise"
                    placeholder="Filter hosts shown..."
                    menuClassName="max-h-80 overflow-y-auto"
                    value={filters.selectedHosts}
                    options={hostOpts}
                    labelExtractor={u => makeFullName(u.firstName, u.lastName) || 'Unknown User'}
                    onAdd={v =>
                      setFilters({ ...filters, selectedHosts: [...filters.selectedHosts, v] })
                    }
                    onRemove={v =>
                      setFilters({
                        ...filters,
                        selectedHosts: filters.selectedHosts.filter(h => h.key !== v.key),
                      })
                    }
                    onClearAll={() => setFilters({ ...filters, selectedHosts: [] })}
                  />
                </BubbleFormGroup>
              )}
            </div>
          </BubbleAccordion>
        </div>
        <div className="ReservablesContent bg-blue-lightest overflow-y-auto flex-grow">
          <div
            className="ReservablesHeader align-with-logo flex items-end px-5 sticky top-0
              left-0 z-10 bg-blue-lightest"
          >
            <div className="mb-3 pl-3 flex-grow flex items-end">
              <ReserveNavigation active="reserve" />
            </div>
            <div
              className="absolute w-full bg-blue-lightest flex border-blue-lightest -mx-5 px-5"
              style={{
                height: 'calc(40px + 0.75rem)',
                bottom: 'calc(-40px - 0.75rem)',
                borderBottomWidth: '0.75rem',
                zIndex: 10,
              }}
            >
              <ReserveSubNavigation active="flagged" property={property} />
            </div>
          </div>
          <div className="ReservablesBody mx-5 content-minus-logo-minus-bar min-w-900">
            <div style={{ marginTop: 'calc(40px + 0.75rem)' }}>
              <BubbleOverlay loading={loading}>
                <BubbleTable
                  responsive
                  striped
                  stickyHeader={182}
                  headVariant="turquoise"
                  fields={tableFields}
                  templates={tableTemplates}
                  items={tableItems}
                  detailsKey={expandedId}
                  detailsRender={tableDetailsRender}
                  detailsClassName="bg-bubblegum-lightest"
                  onRowClick={item => setExpandedId(expandedId === item.id ? undefined : item.id)}
                />
              </BubbleOverlay>
            </div>
            <div className="mt-3">&nbsp;</div>
          </div>
        </div>
        {mode && (
          <BubbleDrawer containerClassName="bg-eggshell" onClose={handleCloseReservationPanel}>
            <ReservationPath
              mode={mode}
              users={users}
              onClose={handleCloseReservationPanel}
              onRefreshReservation={handleSubmitReservation}
              onNavigateToReservation={r => handleOpenReservationPanel({ type: 'edit', edit: r })}
            />
          </BubbleDrawer>
        )}
      </Page>
    </PageWrapper>
  );
}
